import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useBrand } from "../../lib/BrandContext";

/**
 * Public top navigation.
 *
 * Reorganised to reduce top-level clutter — related items live under
 * three lightweight dropdowns:
 *   • Verticals   → For Startups, For Investors, Raise Debt
 *   • Discover    → Verified Marketplace, Trust Score, Diligence
 *   • Learn       → How It Works, Pricing, Resources
 * The single-CTA row on the right stays unchanged (Login / Join /
 * Apply for Funding).
 *
 * Uses a hover + click model — clicking the group label toggles
 * open/close (needed for keyboard + touch); mouse-out closes.
 */
const GROUPS = [
  {
    key: "verticals",
    label: "Verticals",
    items: [
      { to: "/for-startups", label: "For Startups", sub: "Raise equity from vetted investors", testId: "nav-startups" },
      { to: "/for-investors", label: "For Investors", sub: "Access curated private-market deals", testId: "nav-investors" },
      { to: "/money", label: "Raise Debt", sub: "Compare 85+ banks & NBFCs", testId: "nav-raise-debt", debt: true },
    ],
  },
  {
    key: "discover",
    label: "Discover",
    items: [
      { to: "/marketplace", label: "Verified Directory", sub: "Browse funded startups", testId: "nav-marketplace" },
      { to: "/#trust-score", label: "Trust Score", sub: "How VenturaZ scores companies", anchor: true, testId: "nav-trust-score" },
      { to: "/#diligence", label: "Diligence", sub: "Deep DD reports & data-rooms", anchor: true, testId: "nav-diligence" },
    ],
  },
  {
    key: "learn",
    label: "Learn",
    items: [
      { to: "/how-it-works", label: "How It Works", testId: "nav-how" },
      { to: "/pricing", label: "Pricing", testId: "nav-pricing" },
      { to: "/resources", label: "Resources", testId: "nav-resources" },
    ],
  },
];

export default function PublicNavbar() {
  const [openMobile, setOpenMobile] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const rootRef = useRef(null);
  const { user, logout } = useAuth();
  const { logoUrl } = useBrand();
  const nav = useNavigate();
  const dashLink = user?.role === "investor" ? "/investor/dashboard" : (user?.role === "startup" ? "/startup/dashboard" : "/admin/dashboard");

  // Close any open dropdown when the user clicks outside the navbar.
  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpenGroup(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const closeAll = () => { setOpenGroup(null); setOpenMobile(false); };

  return (
    <header ref={rootRef} className="sticky top-0 z-50 glass" data-testid="public-navbar" style={{ borderRadius: 0 }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" onClick={closeAll} className="flex items-center gap-2" data-testid="brand-link">
          {logoUrl ? (
            <img src={logoUrl} alt="VenturaZ" className="h-11 w-auto max-w-[220px] object-contain" data-testid="brand-logo-img" />
          ) : (
            <div className="brand-mark text-2xl md:text-[30px] leading-none">
              <span className="word">VENTURAZ</span>
            </div>
          )}
        </Link>

        {/* Desktop — grouped dropdowns */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-groups">
          {GROUPS.map((g) => (
            <div
              key={g.key}
              className="relative"
              onMouseEnter={() => setOpenGroup(g.key)}
              onMouseLeave={() => setOpenGroup(null)}
            >
              <button
                type="button"
                onClick={() => setOpenGroup(openGroup === g.key ? null : g.key)}
                className={`px-3 py-2 text-sm font-heading transition-colors inline-flex items-center gap-1 ${
                  openGroup === g.key ? "text-ink" : "text-ink-soft"
                } hover:text-[#8A6600]`}
                data-testid={`nav-group-${g.key}`}
                aria-haspopup="true"
                aria-expanded={openGroup === g.key}
              >
                {g.label} <ChevronDown size={13} className={openGroup === g.key ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
              {openGroup === g.key && (
                <div
                  className="absolute left-0 top-full mt-1 w-[320px] rounded-md border border-navy/10 bg-white shadow-[0_18px_40px_rgba(15,61,46,0.14)] py-2"
                  data-testid={`nav-group-panel-${g.key}`}
                >
                  {g.items.map((it) => it.anchor ? (
                    <a
                      key={it.to}
                      href={it.to}
                      data-testid={it.testId}
                      onClick={closeAll}
                      className="block px-4 py-2.5 hover:bg-navy/[0.04] group"
                    >
                      <div className="text-sm font-heading text-ink group-hover:text-[#8A6600]">{it.label}</div>
                      {it.sub && <div className="text-[11px] text-ink-muted mt-0.5">{it.sub}</div>}
                    </a>
                  ) : (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      data-testid={it.testId}
                      onClick={closeAll}
                      className={({ isActive }) => `block px-4 py-2.5 hover:bg-navy/[0.04] group ${it.debt ? "border-l-2 border-[#B8860B]" : ""} ${isActive ? "bg-navy/[0.03]" : ""}`}
                    >
                      <div className={`text-sm font-heading group-hover:text-[#8A6600] ${it.debt ? "text-[#7A4E1D]" : "text-ink"}`}>{it.label}</div>
                      {it.sub && <div className="text-[11px] text-ink-muted mt-0.5">{it.sub}</div>}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              <Link to={dashLink} onClick={closeAll} className="text-sm text-ink font-heading hover:text-[#8A6600] transition-colors px-3" data-testid="nav-my-dashboard">Dashboard</Link>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="px-4 py-2 text-sm bg-white border border-navy/15 text-ink hover:border-gold hover:text-[#8A6600] transition-colors rounded-md"
                data-testid="nav-logout-btn"
              >Sign Out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={closeAll} className="text-sm text-ink font-heading hover:text-[#8A6600] transition-colors px-3" data-testid="nav-login">Login</Link>
              <Link
                to="/money"
                onClick={closeAll}
                className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-heading font-semibold rounded-md text-white transition-all shadow-[0_6px_18px_rgba(122,78,29,0.28)] hover:shadow-[0_10px_24px_rgba(122,78,29,0.36)] hover:-translate-y-[1px]"
                style={{ background: "linear-gradient(135deg, #7A4E1D 0%, #B8860B 100%)" }}
                data-testid="nav-money-cta"
                title="India's honest loan advisor — 85+ banks & NBFCs"
              >
                <span className="brand-mark text-[15px] leading-none" aria-hidden="true">
                  <span className="word" style={{ color: "#FFF3D6" }}>VZ</span>
                </span>
                <span className="hidden lg:inline">Money</span>
                <span className="lg:hidden">Money</span>
                <span className="hidden xl:inline text-[10px] font-mono tracking-widest text-white/80 ml-0.5 border-l border-white/30 pl-2">DEBT</span>
              </Link>
              <Link
                to="/investor/join"
                onClick={closeAll}
                className="hidden xl:inline-flex px-4 py-2 text-sm font-heading font-semibold rounded-md bg-gold text-navy hover:bg-yellow-400 transition-colors shadow-[0_6px_18px_rgba(212,175,55,0.28)]"
                data-testid="nav-join-investor"
              >Join as Investor</Link>
              <Link
                to="/startup/apply"
                onClick={closeAll}
                className="px-5 py-2 bg-navy text-white text-sm font-heading font-semibold hover:bg-[#256E58] transition-colors rounded-md shadow-[0_8px_24px_rgba(15,61,46,0.18)]"
                data-testid="nav-apply-funding"
              >Apply for Funding</Link>
            </>
          )}
        </div>

        <button onClick={() => setOpenMobile(!openMobile)} className="lg:hidden text-ink" data-testid="mobile-menu-toggle">
          {openMobile ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile — flattened list */}
      {openMobile && (
        <div className="lg:hidden glass border-t border-navy/10" data-testid="mobile-menu-panel">
          <div className="px-6 py-4 flex flex-col gap-4">
            {GROUPS.map((g) => (
              <div key={g.key}>
                <div className="text-[10px] font-mono tracking-[0.28em] uppercase text-ink-muted mb-2">{g.label}</div>
                <div className="flex flex-col gap-1">
                  {g.items.map((it) => it.anchor ? (
                    <a key={it.to} href={it.to} onClick={closeAll} data-testid={it.testId} className={`text-sm font-heading py-1 ${it.debt ? "text-[#7A4E1D]" : "text-ink"}`}>{it.label}</a>
                  ) : (
                    <Link key={it.to} to={it.to} onClick={closeAll} data-testid={it.testId} className={`text-sm font-heading py-1 ${it.debt ? "text-[#7A4E1D]" : "text-ink"}`}>{it.label}</Link>
                  ))}
                </div>
              </div>
            ))}
            <div className="h-px bg-navy/10" />
            {user ? (
              <>
                <Link to={dashLink} onClick={closeAll} className="text-ink font-heading text-sm">Dashboard</Link>
                <button onClick={async () => { await logout(); closeAll(); nav("/"); }} className="text-left text-ink-soft font-heading text-sm">Sign Out</button>
              </>
            ) : (
              <div className="flex flex-col gap-2 pt-1">
                <Link to="/login" onClick={closeAll} className="text-ink font-heading text-sm">Login</Link>
                <Link to="/investor/join" onClick={closeAll} className="text-ink font-heading text-sm border border-navy/15 rounded-md px-3 py-2 text-center">Join as Investor</Link>
                <Link to="/startup/apply" onClick={closeAll} className="bg-navy text-white font-heading text-sm rounded-md px-3 py-2 text-center">Apply for Funding</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
